import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, Search, MousePointerClick, FileQuestion, Download, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

interface TopSearch {
  query: string;
  search_count: number;
  avg_results: number;
  click_through_rate: number;
  avg_time_to_click_ms: number;
}

interface ZeroResultSearch {
  query: string;
  search_count: number;
  last_searched: string;
}

interface TrendingSearch {
  query: string;
  recent_count: number;
  previous_count: number;
  growth_rate: number;
}

export const SearchAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30');
  const [topSearches, setTopSearches] = useState<TopSearch[]>([]);
  const [zeroResults, setZeroResults] = useState<ZeroResultSearch[]>([]);
  const [trending, setTrending] = useState<TrendingSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSearches, setTotalSearches] = useState(0);
  const [avgCTR, setAvgCTR] = useState(0);
  const { toast } = useToast();

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load top searches
      const { data: topData, error: topError } = await supabase.rpc('get_top_search_queries', {
        days_ago: parseInt(timeRange),
        limit_count: 20
      });

      if (topError) throw topError;
      setTopSearches(topData || []);

      // Load zero result searches
      const { data: zeroData, error: zeroError } = await supabase.rpc('get_zero_result_searches', {
        days_ago: parseInt(timeRange),
        limit_count: 20
      });

      if (zeroError) throw zeroError;
      setZeroResults(zeroData || []);

      // Load trending searches
      const { data: trendingData, error: trendingError } = await supabase.rpc('get_trending_searches', {
        limit_count: 10
      });

      if (trendingError) throw trendingError;
      setTrending(trendingData || []);

      // Calculate stats
      const totalCount = topData?.reduce((sum, item) => sum + Number(item.search_count), 0) || 0;
      const avgClickRate = topData?.reduce((sum, item) => sum + Number(item.click_through_rate), 0) / (topData?.length || 1);
      
      setTotalSearches(totalCount);
      setAvgCTR(Number(avgClickRate.toFixed(1)));

    } catch (error) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error loading analytics",
        description: "Failed to load search analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const exportToCSV = () => {
    const csvContent = [
      ['Query', 'Searches', 'Avg Results', 'CTR %', 'Avg Time to Click (ms)'],
      ...topSearches.map(s => [
        s.query,
        s.search_count,
        s.avg_results,
        s.click_through_rate,
        s.avg_time_to_click_ms || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `search-analytics-${timeRange}days.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "Search analytics exported to CSV",
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Search Analytics</h2>
          <p className="text-muted-foreground">Monitor search queries and identify content opportunities</p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadAnalytics}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Searches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{totalSearches}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Click-Through Rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MousePointerClick className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{avgCTR}%</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Zero Result Searches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-destructive" />
              <span className="text-2xl font-bold">{zeroResults.length}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Top Search Term</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              <span className="text-lg font-semibold truncate">
                {topSearches[0]?.query || 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Searches Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Top Search Queries
          </CardTitle>
          <CardDescription>Most popular searches with click-through rates</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Query</TableHead>
                <TableHead className="text-right">Searches</TableHead>
                <TableHead className="text-right">Avg Results</TableHead>
                <TableHead className="text-right">CTR %</TableHead>
                <TableHead className="text-right">Avg Time to Click</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topSearches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No search data available for this period
                  </TableCell>
                </TableRow>
              ) : (
                topSearches.map((search, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{search.query}</TableCell>
                    <TableCell className="text-right">{search.search_count}</TableCell>
                    <TableCell className="text-right">{search.avg_results}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={search.click_through_rate > 50 ? "default" : "secondary"}>
                        {search.click_through_rate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {search.avg_time_to_click_ms ? `${(search.avg_time_to_click_ms / 1000).toFixed(1)}s` : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Zero Result Searches */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-destructive" />
              Zero Result Searches
            </CardTitle>
            <CardDescription>Content gaps - what users want but can't find</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Last Searched</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zeroResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No zero-result searches found
                    </TableCell>
                  </TableRow>
                ) : (
                  zeroResults.map((search, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{search.query}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="destructive">{search.search_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {new Date(search.last_searched).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Trending Searches */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Trending Searches
            </CardTitle>
            <CardDescription>Rising topics (last 7 days vs previous 7 days)</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Recent</TableHead>
                  <TableHead className="text-right">Growth</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trending.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No trending searches found
                    </TableCell>
                  </TableRow>
                ) : (
                  trending.map((search, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{search.query}</TableCell>
                      <TableCell className="text-right">{search.recent_count}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={search.growth_rate > 100 ? "default" : "secondary"}>
                          {search.growth_rate > 0 ? '+' : ''}{search.growth_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
